import { Button, Form, FormInstance, Input, InputNumber, Typography } from "antd";
import { ApiService } from "api/api.service";
import { CaseSchemaDto } from "dtos/leadCases.dto";
import { TbPlus, TbX } from "react-icons/tb";
import { numberParser, phoneFormatter, validateNumber } from "utils/functions";

interface UpdateFormProps {
  form: FormInstance;
  data: Omit<CaseSchemaDto, "logs" | "remarks"> | undefined;
  onSuccess?: () => void;
}

export const UpdateForm = ({ data, form, onSuccess }: UpdateFormProps) => {
  const patchCase = ApiService.lead.useUpdateCase();

  return (
    <Form
      disabled={data?.hold}
      form={form}
      preserve={false}
      layout="vertical"
      initialValues={data}
      onFinish={(values) => {
        const phone = values?.phone ? `${phoneFormatter(values?.phone)}` : "";
        patchCase.mutate({ _id: data?._id, ...values, phone }, { onSuccess });
      }}
    >
      <div className="grid gap-2 grid-cols-2">
        <Form.Item<CaseSchemaDto> name={"first_name"} label="First Name">
          <Input />
        </Form.Item>
        <Form.Item<CaseSchemaDto> name={"last_name"} label="Last Name">
          <Input />
        </Form.Item>
      </div>
      <Form.Item<CaseSchemaDto> name={"business_name"} label="Business Name">
        <Input />
      </Form.Item>
      <Form.Item<CaseSchemaDto> name={"email"} label="Email" rules={[{ type: "email" }]}>
        <Input placeholder="Enter Primary Email" />
      </Form.Item>
      <Form.Item noStyle shouldUpdate={(prev, curr) => prev.email !== curr.email}>
        {({ getFieldValue, getFieldsValue }) => {
          const primaryEmail: string = getFieldValue("email") ?? "";
          return (
            <Form.List name="alternate_emails">
              {(fields, { add, remove }) => (
                <div className="-mt-2 mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <Typography.Text className="!text-xs !text-gray-400">Alternate Emails</Typography.Text>
                    {primaryEmail.trim() && (
                      <Button
                        type="link"
                        size="small"
                        className="!p-0 !h-auto !text-indigo-500 !text-xs"
                        icon={<TbPlus className="inline" />}
                        onClick={() => add("")}
                        disabled={data?.hold}
                      >
                        Add more
                      </Button>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    {fields.map((field) => (
                      <div key={field.key} className="flex items-start gap-1">
                        <Form.Item
                          name={field.name}
                          className="!mb-0 flex-1"
                          rules={[{ type: "email", message: "Invalid email" }]}
                        >
                          <Input placeholder="Alternate email" />
                        </Form.Item>
                        <Button
                          type="text"
                          size="small"
                          className="!mt-1 !text-gray-400 hover:!text-red-400"
                          icon={<TbX />}
                          onClick={() => remove(field.name)}
                          disabled={data?.hold}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Form.List>
          );
        }}
      </Form.Item>
      <Form.Item<CaseSchemaDto>
        name={"phone"}
        label="Phone"
        // rules={[
        //   () => ({
        //     validator(_, value) {
        //       if (!value?.trim()?.length || validateNumber(value)) {
        //         return Promise.resolve();
        //       }

        //       return Promise.reject(new Error("Invalid Phone"));
        //     },
        //   }),
        // ]}
      >
        <InputNumber
          type="tel"
          controls={false}
          style={{ width: "100%" }}
          formatter={phoneFormatter}
          parser={numberParser}
          placeholder="Enter Primary Phone Number"
        />
      </Form.Item>
      <Form.Item noStyle shouldUpdate={(prev, curr) => prev.phone !== curr.phone}>
        {({ getFieldValue }) => {
          const primaryPhone = getFieldValue("phone");
          const phoneStr =
            typeof primaryPhone === "number"
              ? String(primaryPhone)
              : typeof primaryPhone === "string"
                ? primaryPhone.replace(/\D/g, "")
                : "";
          return (
            <Form.List name="alternate_phones">
              {(fields, { add, remove }) => (
                <div className="-mt-2 mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <Typography.Text className="!text-xs !text-gray-400">Alternate Phones</Typography.Text>
                    {phoneStr.trim() && (
                      <Button
                        type="link"
                        size="small"
                        className="!p-0 !h-auto !text-indigo-500 !text-xs"
                        icon={<TbPlus className="inline" />}
                        onClick={() => add("")}
                        disabled={data?.hold}
                      >
                        Add more
                      </Button>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    {fields.map((field) => (
                      <div key={field.key} className="flex items-start gap-1">
                        <Form.Item
                          name={field.name}
                          className="!mb-0 flex-1"
                          rules={[
                            () => ({
                              validator(_, value) {
                                const val =
                                  typeof value === "string"
                                    ? value.replace(/\D/g, "")
                                    : typeof value === "number"
                                      ? String(value)
                                      : "";
                                if (val?.length === 11 || val?.length === 0) return Promise.resolve();
                                return Promise.reject(new Error("Invalid Phone"));
                              },
                            }),
                          ]}
                        >
                          <InputNumber
                            type="tel"
                            controls={false}
                            placeholder="Alternate phone"
                            style={{ width: "100%" }}
                            formatter={phoneFormatter}
                            parser={numberParser}
                          />
                        </Form.Item>
                        <Button
                          type="text"
                          size="small"
                          className="!mt-1 !text-gray-400 hover:!text-red-400"
                          icon={<TbX />}
                          onClick={() => remove(field.name)}
                          disabled={data?.hold}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Form.List>
          );
        }}
      </Form.Item>

      <Form.Item<CaseSchemaDto> name={"linkedin"} label="Linkedin">
        <Input placeholder="Enter LinkedIn URL" />
      </Form.Item>
      <Form.Item<CaseSchemaDto> name={"facebook"} label="Facebook">
        <Input placeholder="Enter Facebook URL" />
      </Form.Item>
      <Form.Item<CaseSchemaDto> name={"amount"} label="Amount">
        <Input />
      </Form.Item>
      <Form.Item<CaseSchemaDto> name={"caseNumber"} label="Case Number">
        <Input />
      </Form.Item>
      <Form.Item<CaseSchemaDto> name={"state"} label="State">
        <Input />
      </Form.Item>
      <Form.Item<CaseSchemaDto> name={"city"} label="City">
        <Input />
      </Form.Item>
      <Form.Item<CaseSchemaDto> name={"court"} label="Court">
        <Input />
      </Form.Item>
      <Form.Item<CaseSchemaDto> name={"judge"} label="Judge">
        <Input />
      </Form.Item>
      <Form.Item<CaseSchemaDto> name={"plaintiff"} label="Plaintiff">
        <Input />
      </Form.Item>
      <Form.Item<CaseSchemaDto> name={"plaintiffLawyers"} label="Plaintiff Lawyers">
        <Input />
      </Form.Item>
      <Form.Item<CaseSchemaDto> name={"plaintiffLawFirm"} label="Plaintiff Law Firm">
        <Input />
      </Form.Item>
      <Form.Item<CaseSchemaDto> name={"defendantLawyers"} label="Defendant Lawyers">
        <Input />
      </Form.Item>
      <Form.Item<CaseSchemaDto> name={"defendantLawFirm"} label="Defendant Law Firm">
        <Input />
      </Form.Item>
      <Form.Item<CaseSchemaDto> name={"natureOfSuit"} label="Nature Of Suit">
        <Input />
      </Form.Item>
      <Form.Item<CaseSchemaDto> name={"natureOfSuitId"} label="Nature Of Suit Id">
        <Input />
      </Form.Item>

      <Form.Item<CaseSchemaDto> name={"trade_name"} label="Trade name">
        <Input />
      </Form.Item>
      <Form.Item<CaseSchemaDto> name={"other_business_names"} label="Other business names">
        <Input />
      </Form.Item>
      <Form.Item<CaseSchemaDto> name={"nicknames"} label="Nicknames">
        <Input />
      </Form.Item>
      <Form.Item<CaseSchemaDto> name={"other_business_owners"} label="Other business owners">
        <Input />
      </Form.Item>
      <Form.Item<CaseSchemaDto> name={"type_of_business"} label="Type of business">
        <Input />
      </Form.Item>
      <Form.Item<CaseSchemaDto> name={"case_type"} label="Case type">
        <Input />
      </Form.Item>
      <Form.Item<CaseSchemaDto> name={"issue"} label="Issue">
        <Input.TextArea autoSize={{ minRows: 3, maxRows: 11 }} />
      </Form.Item>
    </Form>
  );
};
